import React, { MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import { Transforms } from 'slate';
import { ReactEditor, useFocused, useSelected, useSlateStatic } from 'slate-react';
import { Overlay, OverlayElement } from 'components/_new/Overlay';
import { ButtonGroup } from 'components/_new/ButtonGroup/ButtonGroup';
import { Button } from 'components/_new/Button';
import { Box, BoxSection } from 'components/_new/Box';
import { Link } from 'components/_new/Link';
import { Icon } from 'components/_new/Icon';
import { LinkElement } from 'components/_new/NewSlateEditor/types';
import { removeLink } from '../../utils/link';
import { LinkEditForm, useEditLinkForm } from './LinkEditForm';

type LinkEditorProps = {
	attributes: any;
	element: LinkElement;
	children?: any;
};

export const LinkEditor = ({ attributes, element, children }: LinkEditorProps) => {
	const { url, openInNewTab } = element;

	const editor = useSlateStatic();
	const selected = useSelected();
	const focused = useFocused();

	const editPopupRef = useRef<OverlayElement>(null);
	const [formState] = useEditLinkForm({
		url,
		openInNewTab: openInNewTab || false,
	});
	const [formData] = formState;

	const handleEditClick: MouseEventHandler = useCallback((event) => {
		if (editPopupRef?.current && event?.currentTarget && event.currentTarget instanceof HTMLElement) {
			editPopupRef.current.toggle(event.currentTarget);
		}
	}, []);

	const handleUnlinkClick = useCallback(() => {
		removeLink(editor);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSaveClick = useCallback(() => {
		const path = ReactEditor.findPath(editor, element);
		const data = formData;
		Transforms.setNodes<LinkElement>(editor, data, { at: path });
		editPopupRef.current?.close();
	}, [editor, element, formData]);

	const inlineEditOverlayRef = useRef<OverlayElement>(null);
	const linkElementRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => {
		if (inlineEditOverlayRef?.current && linkElementRef?.current) {
			if (selected && focused) {
				inlineEditOverlayRef.current.open(linkElementRef.current);
			} else {
				inlineEditOverlayRef.current.close();
			}
		}
	}, [inlineEditOverlayRef, linkElementRef, selected, focused]);

	return (
		<>
			<Overlay ref={editPopupRef}>
				<Box variant="white-bordered" className="editor-popup">
					<BoxSection>
						<div className="flex flex-row gap-1 text-break">
							<span>Link: </span>
							<Link to={url} target="_blank">
								{url}
								<Icon name="external-link" className="ml-1" />
							</Link>
						</div>
						<LinkEditForm formState={formState} />
						<div className="flex flex-row justify-content-end">
							<Button label="Zapisz" onClick={handleSaveClick} />
						</div>
					</BoxSection>
				</Box>
			</Overlay>
			<Overlay ref={inlineEditOverlayRef} placement="top">
				<ButtonGroup
					iconOnly
					variantSize="sm"
					buttons={[
						{
							key: 'edit',
							label: 'Edytuj',
							icon: 'pencil',
							onClick: handleEditClick,
							variant: 'primary',
						},
						{
							key: 'unlink',
							label: 'Usuń łącze',
							icon: 'unlink',
							onClick: handleUnlinkClick,
							variant: 'danger',
						},
					]}
				/>
			</Overlay>
			<a ref={linkElementRef} href={element.url} className="p-link" target="_blank" rel="noreferrer">
				{children}
			</a>
		</>
	);
};
